export { salvarRelatorioPdf } from '../utils/reportFiles';
export { carregarLogoRelatorio, criarCabecalhoRelatorio } from '../utils/reportLayout';
import { criarCabecalhoRelatorio, criarDocumentoRelatorio as documento, criarTabelaRelatorio as tabela } from '../utils/reportLayout';
import { CORES_ZONA_MANEJO } from './stacService';
import { obterNiveisCurvasMestras, selecionarRotulosCurvas } from './terrainContourLabels';

const CORES_DECLIVIDADE = ['#3c9d83', '#7eb77f', '#d0b95d', '#df8b45', '#c95745', '#8d3d55'];
const CLASSES_DECLIVIDADE = ['0–3% Plano', '3–8% Suave', '8–13% Moderado', '13–20% Ondulado', '20–45% Forte', '≥45% Muito forte'];
const numero = (valor, casas = 1) => valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
const titulo = text => ({ text, fontSize: 11, bold: true, color: '#003B73', margin: [0, 12, 0, 8] });
const nota = text => ({ text, fontSize: 8, color: '#666666', margin: [0, 8, 0, 0] });

function legenda(itens) {
  return {
    columns: itens.map(([cor, text]) => ({
      stack: [
        { canvas: [{ type: 'rect', x: 0, y: 0, w: 30, h: 6, color: cor, lineWidth: 0 }] },
        { text, fontSize: 7, margin: [0, 4, 0, 0] },
      ],
    })),
    columnGap: 8,
    margin: [0, 8, 0, 8],
  };
}

function carregarImagem(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Não foi possível preparar um mapa do relatório.'));
    image.src = dataUrl;
  });
}

const projetar = (lng, lat) => [
  lng * Math.PI / 180,
  Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)),
];

export async function renderizarMapaRelatorio({ poligono, mapa, curvas = [], intervalo = 10 }) {
  const geometria = poligono?.type === 'Feature' ? poligono.geometry : poligono;
  const aneis = geometria?.coordinates;
  if (geometria?.type !== 'Polygon' || !aneis?.[0]?.length) throw new Error('Defina um polígono válido para o relatório.');
  const pontos = aneis[0].map(([lng, lat]) => projetar(lng, lat));
  const minX = Math.min(...pontos.map(([x]) => x));
  const maxX = Math.max(...pontos.map(([x]) => x));
  const minY = Math.min(...pontos.map(([, y]) => y));
  const maxY = Math.max(...pontos.map(([, y]) => y));
  if (![minX, maxX, minY, maxY].every(Number.isFinite) || maxX <= minX || maxY <= minY) {
    throw new Error('O perímetro não possui extensão válida para o relatório.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = 1070;
  canvas.height = 640;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Não foi possível criar os mapas do relatório neste dispositivo.');
  const escala = Math.min((canvas.width - 80) / (maxX - minX), (canvas.height - 80) / (maxY - minY));
  const offsetX = (canvas.width - (maxX - minX) * escala) / 2;
  const offsetY = (canvas.height - (maxY - minY) * escala) / 2;
  const pixel = (lng, lat) => {
    const [x, y] = projetar(lng, lat);
    return [offsetX + (x - minX) * escala, offsetY + (maxY - y) * escala];
  };
  const desenharPerimetro = () => {
    ctx.beginPath();
    for (const anel of aneis) {
      anel.forEach(([lng, lat], indice) => {
        const [x, y] = pixel(lng, lat);
        if (indice === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.closePath();
    }
  };
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  desenharPerimetro();
  ctx.clip('evenodd');
  if (mapa) {
    const image = await carregarImagem(mapa.dataUrl);
    const [[sul, oeste], [norte, leste]] = mapa.bounds;
    const [x, y] = pixel(oeste, norte);
    const [fimX, fimY] = pixel(leste, sul);
    ctx.drawImage(image, x, y, fimX - x, fimY - y);
  }
  const mestras = new Set(obterNiveisCurvasMestras(curvas, intervalo));
  for (const curva of curvas) {
    ctx.beginPath();
    curva.positions.forEach(([lat, lng], indice) => {
      const [x, y] = pixel(lng, lat);
      if (indice === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#79502f';
    ctx.lineWidth = mestras.has(curva.nivel) ? 2.2 : 1.2;
    ctx.stroke();
  }
  ctx.font = 'bold 18px sans-serif';
  ctx.textAlign = 'center';
  for (const rotulo of selecionarRotulosCurvas(curvas, intervalo, 15)) {
    const [lat, lng] = rotulo.position;
    const [x, y] = pixel(lng, lat);
    const text = `${numero(rotulo.nivel, 0)} m`;
    const width = ctx.measureText(text).width + 10;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x - width / 2, y - 17, width, 22);
    ctx.fillStyle = '#704525';
    ctx.fillText(text, x, y);
  }
  ctx.restore();
  desenharPerimetro();
  ctx.strokeStyle = '#003B73';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = '#003B73';
  ctx.font = 'bold 20px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('N', canvas.width - 30, 25);
  ctx.beginPath();
  ctx.moveTo(canvas.width - 30, 35);
  ctx.lineTo(canvas.width - 38, 55);
  ctx.lineTo(canvas.width - 22, 55);
  ctx.closePath();
  ctx.fill();
  return canvas.toDataURL('image/png');
}

export async function criarRelatorioNdvi({ contexto, poligono, cena, stats, mapaNdvi, zonas, logo }) {
  const [imagemNdvi, imagemZonas] = await Promise.all([
    renderizarMapaRelatorio({ poligono, mapa: mapaNdvi }),
    renderizarMapaRelatorio({ poligono, mapa: zonas }),
  ]);
  const dataCena = cena.properties?.datetime?.split('T')[0] || 'Não informada';
  const nuvens = cena.properties?.['eo:cloud_cover'];
  const content = [
    criarCabecalhoRelatorio('Relatório de NDVI e Zonas de Manejo', contexto, logo),
    tabela([
      ['Área analisada', `${numero(contexto.areaHa, 2)} ha`],
      ['Cena selecionada', dataCena],
      ['Identificador da cena', cena.id],
      ['Nuvens na cena', Number.isFinite(nuvens) ? `${numero(nuvens)}%` : 'Não informadas'],
      ['Fonte', 'Sentinel-2 L2A (ESA)'],
    ]),
    titulo('NDVI — vigor vegetativo'),
    tabela([
      ['Mínimo', 'Médio', 'Máximo', 'Desvio padrão', 'Pixels válidos'],
      [numero(stats.min, 3), numero(stats.mean, 3), numero(stats.max, 3), numero(stats.std, 3), numero(stats.count, 0)],
    ], true),
    { image: imagemNdvi, fit: [535, 320] },
    legenda([['#d73027', '−1 · solo/água'], ['#fee08b', '0'], ['#1a9850', '+1 · maior vigor']]),
    nota('NDVI = (NIR − vermelho) / (NIR + vermelho). Mapa recortado pelo perímetro selecionado; áreas sem pixels válidos não são classificadas.'),
    { ...titulo('Zonas de manejo — vigor relativo'), pageBreak: 'before' },
    { image: imagemZonas, fit: [535, 320] },
    legenda([[CORES_ZONA_MANEJO.baixo, 'Baixo vigor'], [CORES_ZONA_MANEJO.medio, 'Vigor médio'], [CORES_ZONA_MANEJO.alto, 'Alto vigor']]),
    tabela([
      ['Zona', 'Pixels válidos (%)', 'Critério de classificação'],
      ['Baixo vigor', `${numero(zonas.baixoPct)}%`, `NDVI < ${numero(zonas.mean - zonas.std, 3)}`],
      ['Vigor médio', `${numero(zonas.medioPct)}%`, `Entre ${numero(zonas.mean - zonas.std, 3)} e ${numero(zonas.mean + zonas.std, 3)}`],
      ['Alto vigor', `${numero(zonas.altoPct)}%`, `NDVI > ${numero(zonas.mean + zonas.std, 3)}`],
    ], true),
    nota('Zonas relativas à média ± desvio padrão do NDVI da própria área nesta cena. Uma única imagem não define zonas agronômicas permanentes nem prescrição de insumos. Valide com histórico, solo e observações de campo.'),
    nota('Imagens: Sentinel-2 L2A © ESA • Catálogo: Element84 Earth Search • Processamento: TiTiler.xyz e ClimoraX.'),
  ];
  return documento(content);
}

export async function criarRelatorioTerreno({ contexto, poligono, terreno, intervalo, logo }) {
  const curvas = terreno.curvasPorIntervalo[intervalo];
  if (!curvas) throw new Error('O intervalo de curvas selecionado não está disponível nesta análise.');
  const [altitude, declividade, curvasImagem] = await Promise.all([
    renderizarMapaRelatorio({ poligono, mapa: terreno.mapaAltitude }),
    renderizarMapaRelatorio({ poligono, mapa: terreno.mapaDeclividade }),
    renderizarMapaRelatorio({ poligono, curvas, intervalo }),
  ]);
  const fonte = `Fonte: ${terreno.fonte}. Grade de cálculo: ~${terreno.resolucao} m.${terreno.resolucaoNominal ? ` Resolução nominal da fonte: ~${terreno.resolucaoNominal} m.` : ''}`;
  return documento([
    criarCabecalhoRelatorio('Relatório de Terreno', contexto, logo),
    tabela([['Área analisada', `${numero(contexto.areaHa, 2)} ha`], ['Modelo de elevação', terreno.fonte], ['Intervalo vertical das curvas', `${numero(intervalo, 0)} m`]]),
    titulo('Altitude'),
    tabela([
      ['Mínima', 'Média', 'Máxima', 'Amplitude'],
      [terreno.altitude.min, terreno.altitude.mean, terreno.altitude.max, terreno.altitude.amplitude].map(valor => `${numero(valor)} m`),
    ], true),
    { image: altitude, fit: [535, 320] },
    legenda([['#1c6f7e', `${numero(terreno.altitude.min)} m`], ['#a3b56b', 'Altitude intermediária'], ['#efe5cc', `${numero(terreno.altitude.max)} m`]]),
    nota(fonte),
    { ...titulo('Declividade'), pageBreak: 'before' },
    tabela([['Média', `${numero(terreno.declividade.mean)}%`], ['Máxima estimada', `${numero(terreno.declividade.max)}%`]]),
    { image: declividade, fit: [535, 320] },
    legenda(CORES_DECLIVIDADE.map((cor, indice) => [cor, CLASSES_DECLIVIDADE[indice]])),
    tabela([
      ['Classe de declividade', 'Participação nos pixels válidos'],
      ...CLASSES_DECLIVIDADE.map((classe, indice) => [classe, `${numero(terreno.declividade.classes[indice])}%`]),
    ], true),
    nota('As porcentagens de declividade representam os pixels com vizinhança válida para cálculo, não necessariamente toda a área do perímetro.'),
    { ...titulo('Curvas de nível'), pageBreak: 'before' },
    { text: `Equidistância vertical: ${numero(intervalo, 0)} m`, margin: [0, 0, 0, 8] },
    { image: curvasImagem, fit: [535, 320] },
    nota('Linhas marrons: curvas de nível; linhas mais espessas e rótulos: curvas mestras. Contorno azul: perímetro analisado.'),
    ...(!curvas.length ? [nota('Não há curvas neste intervalo dentro da área analisada. Isso não significa ausência de variações no terreno.')] : []),
    nota(fonte),
    nota('Diagnóstico preliminar. A grade de cálculo e o intervalo entre curvas não representam precisão vertical. O modelo pode incluir vegetação e construções e não substitui levantamento topográfico para projetos ou locação em campo.'),
    ...(terreno.atribuicao ? [nota(terreno.atribuicao)] : []),
  ]);
}
